import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Search,
  Filter,
  Mail,
  MailX,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  CreditCard,
  Loader2,
  ChevronDown,
  RefreshCw,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const outcomeConfig = {
  laag_risico: { label: "Laag risico", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  twijfelgeval: { label: "Twijfelgeval", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  verhoogd_risico: { label: "Verhoogd risico", icon: XCircle, color: "text-red-600", bg: "bg-red-50" }
};

const LeadsSection = ({ password }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    paid: "",
    outcome: "",
    email_sent: "",
    unsubscribed: ""
  });
  const [searchTerm, setSearchTerm] = useState("");

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ password });
      
      if (filters.paid !== "") params.append("paid", filters.paid);
      if (filters.outcome) params.append("outcome", filters.outcome);
      if (filters.email_sent !== "") params.append("email_sent", filters.email_sent);
      if (filters.unsubscribed !== "") params.append("unsubscribed", filters.unsubscribed);
      
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/leads?${params.toString()}&limit=100`),
        axios.get(`${API_URL}/admin/leads/stats?password=${password}`)
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Kon leads niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [filters, password]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-zinc-500">Totaal Leads</span>
            </div>
            <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.total_leads}</p>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-zinc-500">Betaald</span>
            </div>
            <p className="text-2xl font-outfit font-medium text-emerald-600">{stats.paid}</p>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-zinc-500">Onbetaald</span>
            </div>
            <p className="text-2xl font-outfit font-medium text-amber-600">{stats.unpaid}</p>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-zinc-500">Actief</span>
            </div>
            <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.active}</p>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <MailX className="w-4 h-4 text-red-600" />
              <span className="text-xs text-zinc-500">Uitgeschreven</span>
            </div>
            <p className="text-2xl font-outfit font-medium text-red-600">{stats.unsubscribed}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-zinc-200/60 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-sm font-medium text-zinc-700">Filters:</span>
          </div>
          
          <Select 
            value={filters.paid || "all"} 
            onValueChange={(v) => setFilters({...filters, paid: v === "all" ? "" : v})}
          >
            <SelectTrigger className="w-[140px] rounded-lg">
              <SelectValue placeholder="Betaalstatus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="true">Betaald</SelectItem>
              <SelectItem value="false">Onbetaald</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={filters.outcome || "all"} 
            onValueChange={(v) => setFilters({...filters, outcome: v === "all" ? "" : v})}
          >
            <SelectTrigger className="w-[160px] rounded-lg">
              <SelectValue placeholder="Risico niveau" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle niveaus</SelectItem>
              <SelectItem value="laag_risico">Laag risico</SelectItem>
              <SelectItem value="twijfelgeval">Twijfelgeval</SelectItem>
              <SelectItem value="verhoogd_risico">Verhoogd risico</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={filters.unsubscribed || "all"} 
            onValueChange={(v) => setFilters({...filters, unsubscribed: v === "all" ? "" : v})}
          >
            <SelectTrigger className="w-[160px] rounded-lg">
              <SelectValue placeholder="Email status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="false">Actief</SelectItem>
              <SelectItem value="true">Uitgeschreven</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Zoek op naam of email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg"
              />
            </div>
          </div>

          <Button
            variant="outline"
            onClick={fetchLeads}
            className="rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-outfit font-medium text-zinc-900">
            Leads ({filteredLeads.length})
          </h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">Geen leads gevonden</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="font-medium">Naam</TableHead>
                  <TableHead className="font-medium">Email</TableHead>
                  <TableHead className="font-medium">Score</TableHead>
                  <TableHead className="font-medium">Risico</TableHead>
                  <TableHead className="font-medium">Betaald</TableHead>
                  <TableHead className="font-medium">Email Status</TableHead>
                  <TableHead className="font-medium">Datum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => {
                  const outcome = outcomeConfig[lead.outcome] || outcomeConfig.twijfelgeval;
                  const OutcomeIcon = outcome.icon;
                  
                  return (
                    <TableRow key={lead.id} className="hover:bg-zinc-50">
                      <TableCell className="font-medium text-zinc-900">
                        {lead.name || <span className="text-zinc-400 italic">Onbekend</span>}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-600">
                        {lead.email}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-zinc-900">{lead.normalized_score}</span>
                        <span className="text-zinc-400">/100</span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${outcome.bg} ${outcome.color}`}>
                          <OutcomeIcon className="w-3 h-3" />
                          {outcome.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        {lead.premium_unlocked ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Ja
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 bg-zinc-100 px-2 py-1 rounded-full">
                            <XCircle className="w-3 h-3" />
                            Nee
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.unsubscribed ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                            <MailX className="w-3 h-3" />
                            Uitgeschreven
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                            <Mail className="w-3 h-3" />
                            Actief
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-500">
                        {formatDate(lead.timestamp)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsSection;
